import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, CircularProgress } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { DiamDrawer } from '../DiamDrawer/DiamDrawer';
import { Stepper } from '../../../Stepper/Stepper';
import { RoleDetailsStep } from './steps/RoleDetailsStep';
import { ContentsStep } from './steps/ContentsStep';
import { ModifiersStep } from './steps/ModifiersStep';
import { CreateRoleDrawerProps, CreateRoleFormValues } from './types';
import {
  DEFAULT_CREATE_ROLE_VALUES,
  createRoleValidationSchema,
} from './validationSchema';
import {
  CREATE_ROLE_STEPS,
  CREATE_ROLE_STEP_IDS,
  CREATE_ROLE_STEP_LABELS,
  getCreateRoleStepFields,
} from './stepsConfig';
import { FooterActions } from './styles';
import { useCreateRoleOptions } from './useCreateRoleOptions';
import { useCreateTeamRole } from './useCreateTeamRole';

const FIRST_STEP_INDEX = 0;
const LAST_STEP_INDEX = CREATE_ROLE_STEPS.length - 1;

export function CreateRoleDrawer({
  isOpen,
  handleDrawerClose,
}: CreateRoleDrawerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(FIRST_STEP_INDEX);

  const {
    teamOptions,
    contentsTree,
    functionalRoles,
    isLoadingTeams,
    isLoadingContents,
    isErrorTeams,
    isErrorContents,
    teamsError,
    contentsError,
  } = useCreateRoleOptions(isOpen);

  const createTeamRole = useCreateTeamRole(functionalRoles);

  const { control, trigger, reset, handleSubmit, clearErrors } =
    useForm<CreateRoleFormValues>({
      resolver: yupResolver(createRoleValidationSchema),
      defaultValues: DEFAULT_CREATE_ROLE_VALUES,
      mode: 'onChange',
    });

  const currentStep = CREATE_ROLE_STEPS[step];
  const isFirstStep = step === FIRST_STEP_INDEX;
  const isLastStep = step === LAST_STEP_INDEX;

  useEffect(() => {
    if (!isOpen || !isErrorTeams) {
      return;
    }

    enqueueSnackbar(getApiErrorMessage(teamsError, 'Failed to load teams'), {
      variant: 'error',
    });
  }, [enqueueSnackbar, isErrorTeams, isOpen, teamsError]);

  useEffect(() => {
    if (!isOpen || !isErrorContents) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(contentsError, 'Failed to load role contents'),
      { variant: 'error' },
    );
  }, [contentsError, enqueueSnackbar, isErrorContents, isOpen]);

  const resetDrawer = () => {
    reset(DEFAULT_CREATE_ROLE_VALUES);
    setStep(FIRST_STEP_INDEX);
  };

  const onClose = () => {
    resetDrawer();
    handleDrawerClose();
  };

  const goNext = async () => {
    const fields = getCreateRoleStepFields(step);
    const isValid = fields.length === 0 ? true : await trigger(fields);
    if (!isValid || isLastStep) return;
    setStep(prev => prev + 1);
  };

  const goPrevious = () => {
    if (isFirstStep) return;
    setStep(prev => prev - 1);
  };

  const onSubmit = async (data: CreateRoleFormValues) => {
    try {
      await createTeamRole.mutateAsync(data);
      onClose();
      enqueueSnackbar('Role successfully created', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to create role'), {
        variant: 'error',
      });
    }
  };

  const handleCreate = () => {
    clearErrors();
    handleSubmit(onSubmit)();
  };

  function renderStepContent() {
    switch (currentStep.id) {
      case CREATE_ROLE_STEP_IDS.roleDetails:
        return (
          <RoleDetailsStep
            control={control}
            teamOptions={teamOptions}
            isLoadingTeams={isLoadingTeams}
          />
        );
      case CREATE_ROLE_STEP_IDS.contents:
        return (
          <ContentsStep
            control={control}
            contentsTree={contentsTree}
            isLoadingContents={isLoadingContents}
          />
        );
      case CREATE_ROLE_STEP_IDS.modifiers:
        return <ModifiersStep />;
      default:
        return null;
    }
  }

  const isSubmitting = createTeamRole.isPending;

  let primaryActionLabel = 'Next';
  if (isLastStep) {
    primaryActionLabel = isSubmitting ? 'Creating…' : 'Create';
  }

  return (
    <DiamDrawer
      isOpen={isOpen}
      handleDrawerClose={onClose}
      hasHide
      title="Create New Role"
      sectionTitle={currentStep.sectionTitle}
      confirmOnClose
      confirmTitle="Are you sure you want to cancel?"
      confirmContent="All the details provided will be lost."
      confirmButtonText="Cancel adding Role"
      cancelButtonText="Dismiss"
      headerCenter={
        <Stepper
          steps={CREATE_ROLE_STEP_LABELS}
          step={step}
          next={goNext}
          previous={goPrevious}
        />
      }
    >
      {renderStepContent()}

      <FooterActions>
        {!isFirstStep && (
          <Button
            variant="outlined"
            color="primary"
            onClick={goPrevious}
            disabled={isSubmitting}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={isLastStep ? handleCreate : goNext}
          disabled={isSubmitting || (isFirstStep && isLoadingTeams)}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
        >
          {primaryActionLabel}
        </Button>
      </FooterActions>
    </DiamDrawer>
  );
}
